/**
 * The weekly availability a resource is set up with in the wizard.
 *
 * The stored shape is the Resource entity's: `workingDays` ({ monday: true, … })
 * with one `workingHours` ({ start, end }) for all of them, or — once
 * `useAdvancedSchedule` is on — `advancedSchedule`, one { enabled, start, end }
 * per day. These helpers never invent a new shape; they read and write that one.
 */

/** Monday first, the way a Romanian week is written. */
export const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

/** The two week shapes the wizard offers as one-click presets. */
export const SCHEDULE_PRESETS = {
  weekdays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  weekend: ["saturday", "sunday"],
};

/** A full `workingDays` object with exactly `days` switched on. */
export const workingDaysFor = (days) =>
  Object.fromEntries(DAY_KEYS.map((day) => [day, days.includes(day)]));

/** The days switched on, in week order. */
export const activeDays = (resource) =>
  resource?.useAdvancedSchedule
    ? DAY_KEYS.filter((day) => resource.advancedSchedule?.[day]?.enabled)
    : DAY_KEYS.filter((day) => resource?.workingDays?.[day]);

/** Which preset the selected days match, or "custom" when they match none. */
export const presetOf = (workingDays) => {
  const selected = DAY_KEYS.filter((day) => workingDays?.[day]).join();
  return (
    Object.keys(SCHEDULE_PRESETS).find(
      (preset) => SCHEDULE_PRESETS[preset].join() === selected,
    ) ?? "custom"
  );
};

/**
 * The selected days grouped into runs of consecutive days, so a summary can say
 * "Monday – Friday" instead of listing five names. Sunday does not wrap round
 * to Monday: "Sunday, Monday" reads better than "Sunday – Monday".
 */
export const dayRuns = (days) => {
  const runs = [];
  DAY_KEYS.forEach((day, index) => {
    if (!days.includes(day)) return;
    const last = runs[runs.length - 1];
    if (last && DAY_KEYS.indexOf(last[last.length - 1]) === index - 1) {
      last.push(day);
    } else {
      runs.push([day]);
    }
  });
  return runs;
};

/**
 * Per-day hours seeded from the simple schedule, so switching to "different
 * hours per day" starts from what was already chosen rather than a blank week.
 */
export const advancedFromSimple = (workingDays, workingHours) =>
  Object.fromEntries(
    DAY_KEYS.map((day) => [
      day,
      {
        enabled: Boolean(workingDays?.[day]),
        start: workingHours?.start || "09:00",
        end: workingHours?.end || "17:00",
      },
    ]),
  );

/**
 * The week in one line — "Monday – Friday, 09:00 – 17:00" — for the summary
 * under the editor and the rows of a team list. Runs of three or more days are
 * written as a range; shorter ones are listed.
 */
export const describeSchedule = (resource, t) => {
  const days = activeDays(resource);
  if (days.length === 0) return t("Dashboard.Setup.team.noDays");

  const name = (day) => t(`Dashboard.Setup.days.long.${day}`);
  const dayText = dayRuns(days)
    .map((run) =>
      run.length >= 3
        ? `${name(run[0])} – ${name(run[run.length - 1])}`
        : run.map(name).join(", "),
    )
    .join(", ");

  if (resource.useAdvancedSchedule) {
    return `${dayText} · ${t("Dashboard.Setup.team.perDayShort")}`;
  }
  const { start, end } = resource.workingHours || {};
  return `${dayText}, ${start} – ${end}`;
};

/**
 * Whether a working day ends after it starts. `<input type="time">` always
 * produces zero-padded 24-hour "HH:MM", so the strings compare correctly.
 */
export const isTimeRangeValid = (start, end) =>
  Boolean(start && end && start < end);
